import React from "react";
import styled from "styled-components";
import Icon from "shared/components/Icon";
import { COLORS, colors } from "shared/config/constants";

export type StatusType = "pass" | "fail" | "warn" | "pending";

interface StatusIconProps {
  status: StatusType;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Returns the appropriate color based on status
 */
export const getStatusColor = (status) => {
  if (status === "pass") {
    return colors.success[400];
  }
  if (status === "fail") {
    return COLORS.lightRed2;
  }
  if (status === "warn") {
    return COLORS.orange;
  }
  if (status === "pending") {
    return colors.neutral[400]; // Gray border for pending status
  }
  return "inherit";
};
/**
 * Reusable component that renders an icon based on status
 */
const StatusIcon: React.FC<StatusIconProps> = ({
  status,
  className = "",
  style = {}
}) => {
  switch (status) {
    case "pass":
      return <Icon.CheckGreenSmall className={className} style={style} />;
    case "fail":
      return <Icon.CrossRedSmall className={className} style={style} />;
    case "warn":
      return (
        <Icon.OrangeExclamationSmall className={className} style={style} />
      );
    case "pending":
      return null;
    default:
      return null;
  }
};

export default StatusIcon;

const LG_TONES = {
  pass: { circle: "#D8F3EA", glyph: "#03684A", path: "M20 6L9 17l-5-5" },
  warn: { circle: "#FBEEDA", glyph: "#8A5A00", path: "M12 4v9M12 19.5v.5" },
  fail: { circle: "#FAE4E9", glyph: "#8E1B33", path: "M18 6L6 18M6 6l12 12" },
  pending: { circle: "#EDECF2", glyph: "#5B5578", path: "" },
};

export type LgStatusTone = keyof typeof LG_TONES;

/** Text ink matching the disc glyph, so a verdict word and its disc agree. */
export const lgToneInk = (tone: string): string =>
  LG_TONES[lgStatusTone(tone)].glyph;

export const lgStatusTone = (status): LgStatusTone =>
  status in LG_TONES ? status : "pending";

const LgCircle = styled.span<{ $tone: LgStatusTone; $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${({ $tone }) => LG_TONES[$tone].circle};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
`;

export const LgStatusIcon = ({
  status,
  size = 17,
  testId,
}: {
  status: string;
  size?: number;
  testId?: string;
}) => {
  const tone = lgStatusTone(status);
  const { glyph, path } = LG_TONES[tone];
  const glyphSize = Math.round((size * 9) / 17);
  return (
    <LgCircle
      $tone={tone}
      $size={size}
      data-testid={testId ?? `lg-status-icon-${tone}`}
    >
      {path && (
        <svg
          width={glyphSize}
          height={glyphSize}
          viewBox="0 0 24 24"
          fill="none"
          stroke={glyph}
          strokeWidth="3.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d={path} />
        </svg>
      )}
    </LgCircle>
  );
};
